import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import {
  NgCommonModule,
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { IOrganisation } from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  snapshot,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { first, isString, sortBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { OrganisationStatusComponent } from '../organisation-status/organisation-status.component';

@Component({
  selector: 'pr-organisation-selector',
  templateUrl: './organisation-selector.component.html',
  styleUrls: ['./organisation-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    OrganisationStatusComponent,
  ],
})
export class OrganisationSelectorComponent {
  searchCtrl = new TypedFormControl<string>('');
  organisationCtrl = new TypedFormControl<WithRef<IOrganisation>>();
  filteredOrganisations$: Observable<WithRef<IOrganisation>[]>;

  organisations = input<WithRef<IOrganisation>[], WithRef<IOrganisation>[]>(
    [],
    {
      transform: (organisations?: WithRef<IOrganisation>[]) =>
        sortBy(
          (organisations ?? []).filter(undeletedFilter).map((organisation) => ({
            ...organisation,
            name: organisation.name.trim(),
          })),
          'name'
        ),
    }
  );

  @Output()
  organisationSelected = new EventEmitter<WithRef<IOrganisation>>();

  @Input()
  set selectedOrganisation(organisation: WithRef<IOrganisation>) {
    if (organisation) {
      this.organisationCtrl.setValue(organisation, { emitEvent: false });
    }
  }

  constructor() {
    this.organisationCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((organisation) =>
        this.organisationSelected.emit(organisation)
      );

    this.filteredOrganisations$ = combineLatest([
      toObservable(this.organisations),
      validFormControlChanges$(this.searchCtrl).pipe(startWith('')),
    ]).pipe(
      map(([organisations, search]) =>
        this._getFilteredOrganisations(organisations, search)
      )
    );
  }

  async selectFirstOption(): Promise<void> {
    const firstOption = await snapshot(
      this.filteredOrganisations$.pipe(
        map((organisations) => first(organisations))
      )
    );
    if (firstOption) {
      this.organisationSelected.emit(firstOption);
    }
  }

  compareFn(
    value: WithRef<IOrganisation>,
    assignee: WithRef<IOrganisation>
  ): boolean {
    if (!value || !assignee) {
      return false;
    }

    return isSameRef(value, assignee);
  }

  private _getFilteredOrganisations(
    organisations: WithRef<IOrganisation>[],
    search?: string
  ): WithRef<IOrganisation>[] {
    if (!search || !isString(search)) {
      return organisations;
    }
    const searchValue = search?.trim().toLowerCase();
    return organisations.filter((organisation) =>
      organisation.name.toLowerCase().includes(searchValue)
    );
  }
}
